// Deterministic avatar generator for usernames
// Bright, high-contrast patterns optimized for dark UI backgrounds

function djb2(str: string): number {
  let h = 5381;
  for (let i = 0; i < str.length; i++) {
    h = ((h << 5) + h) + str.charCodeAt(i);
    h |= 0;
  }
  return h >>> 0;
}

function hsl(h: number, s: number, l: number): string {
  return `hsl(${Math.round(h)}, ${Math.round(s)}%, ${Math.round(l)}%)`;
}

// Create a symmetric 5x5 grid where the first 3 columns mirror to the last 2
function patternBits(seed: number): boolean[] {
  const bits: boolean[] = [];
  let n = seed;
  for (let i = 0; i < 15; i++) {
    n ^= n << 13; n ^= n >>> 17; n ^= n << 5;
    bits.push(((n >>> 0) & 1) === 1);
  }
  return bits;
}

export function avatarDataUriForName(name: string | undefined | null, opts?: { size?: number }): string {
  const safeName = (name || 'player').trim().toLowerCase();
  const size = Math.max(16, Math.min(512, opts?.size ?? 100));
  const seed = djb2(safeName);

  // Bright, poppy palette — high saturation, mid-to-high lightness
  const baseHue = seed % 360;
  const hue2 = (baseHue + 140) % 360;
  const hue3 = (baseHue + 280) % 360;
  const bg = hsl(baseHue, 85, 82);    // bright backdrop for a dark app background
  const c1 = hsl(baseHue, 85, 55);
  const c2 = hsl(hue2, 85, 58);
  const c3 = hsl(hue3, 85, 60);

  // Helpers to pick deterministic values
  const pick = (mod: number, offset = 0) => ((seed >>> offset) % mod);
  const vb = 100;

  const pieces: string[] = [];
  const style = pick(9); // choose a pattern style

  if (style === 0) {
    // Identicon grid (symmetric 5x5)
    const bits = patternBits(seed);
    const cell = 16;
    const pad = 10;
    for (let row = 0; row < 5; row++) {
      for (let col = 0; col < 5; col++) {
        const sourceCol = col < 3 ? col : 4 - col;
        const bitIndex = row * 3 + sourceCol;
        if (bits[bitIndex]) {
          const x = pad + col * cell;
          const y = pad + row * cell;
          const color = ((seed >> (row + col)) & 1) ? c2 : c1;
          pieces.push(`<rect x="${x}" y="${y}" width="${cell}" height="${cell}" rx="3" ry="3" fill="${color}"/>`);
        }
      }
    }
  } else if (style === 1) {
    // Vertical stripes (thick)
    const n = 3 + pick(4, 3); // 3..6
    const w = vb / n;
    for (let i = 0; i < n; i++) {
      const color = i % 3 === 0 ? c1 : (i % 3 === 1 ? c2 : c3);
      pieces.push(`<rect x="${i * w}" y="0" width="${w}" height="${vb}" fill="${color}"/>`);
    }
  } else if (style === 2) {
    // Horizontal stripes (thick)
    const n = 3 + pick(4, 4);
    const h = vb / n;
    for (let i = 0; i < n; i++) {
      const color = i % 3 === 0 ? c1 : (i % 3 === 1 ? c2 : c3);
      pieces.push(`<rect x="0" y="${i * h}" width="${vb}" height="${h}" fill="${color}"/>`);
    }
  } else if (style === 3) {
    // Diagonal stripes
    const stripeW = 12 + pick(8, 5); // 12..19
    const colors = [c1, c2, c3];
    for (let i = -vb; i < vb * 2; i += stripeW) {
      const idx = ((i / stripeW) | 0) % 3;
      const normIdx = idx < 0 ? idx + 3 : idx;
      const color = colors[normIdx];
      pieces.push(`<rect x="${i}" y="-50" width="${stripeW}" height="${vb * 3}" fill="${color}" transform="rotate(45 ${vb/2} ${vb/2})"/>`);
    }
  } else if (style === 4) {
    // Pinstripes (thin vertical)
    const stripeW = 4;
    for (let x = 0; x < vb; x += stripeW * 2) {
      const color = ((x / stripeW) % 2) ? c2 : c1;
      pieces.push(`<rect x="${x}" y="0" width="${stripeW}" height="${vb}" fill="${color}"/>`);
    }
  } else if (style === 5) {
    // Polka dots grid
    const dots = 5 + pick(3, 6); // 5..7 per row
    const gap = vb / (dots + 1);
    const r = Math.max(3, gap * 0.35);
    for (let row = 1; row <= dots; row++) {
      for (let col = 1; col <= dots; col++) {
        const color = ((row + col) % 3 === 0) ? c3 : ((row + col) % 2 === 0 ? c2 : c1);
        pieces.push(`<circle cx="${col * gap}" cy="${row * gap}" r="${r}" fill="${color}"/>`);
      }
    }
  } else if (style === 6) {
    // Half split (vertical or horizontal)
    const vertical = ((seed >> 7) & 1) === 1;
    const first = c1, second = c2;
    if (vertical) {
      pieces.push(`<rect x="0" y="0" width="${vb/2}" height="${vb}" fill="${first}"/>`);
      pieces.push(`<rect x="${vb/2}" y="0" width="${vb/2}" height="${vb}" fill="${second}"/>`);
    } else {
      pieces.push(`<rect x="0" y="0" width="${vb}" height="${vb/2}" fill="${first}"/>`);
      pieces.push(`<rect x="0" y="${vb/2}" width="${vb}" height="${vb/2}" fill="${second}"/>`);
    }
  } else if (style === 7) {
    // Diagonal bicolor
    const first = c1, second = c2;
    pieces.push(`<polygon points="0,0 ${vb},0 0,${vb}" fill="${first}"/>`);
    pieces.push(`<polygon points="${vb},0 ${vb},${vb} 0,${vb}" fill="${second}"/>`);
  } else {
    // Tricolor flag (vertical)
    const w = vb / 3;
    pieces.push(`<rect x="0" y="0" width="${w}" height="${vb}" fill="${c1}"/>`);
    pieces.push(`<rect x="${w}" y="0" width="${w}" height="${vb}" fill="${c2}"/>`);
    pieces.push(`<rect x="${2*w}" y="0" width="${w}" height="${vb}" fill="${c3}"/>`);
  }

  const svg = `<?xml version="1.0" encoding="UTF-8"?>
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${vb} ${vb}" width="${size}" height="${size}" role="img" aria-label="avatar">
  <rect width="${vb}" height="${vb}" fill="${bg}"/>
  ${pieces.join('\n  ')}
</svg>`;

  return 'data:image/svg+xml,' + encodeURIComponent(svg);
}

export default avatarDataUriForName;
